import React, { useState, useCallback } from 'react';
import { Mail, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import GmailOAuthService from '../../services/gmail/GmailOAuthService';
import { generateOAuthState, storeOAuthState } from '../../utils/oauth/oauthUtils';

/**
 * Simple Gmail OAuth Component (Debug Version)
 * This is a simplified version to test if the component renders without errors
 */
const GmailOAuthSimple = ({
  onSuccess,
  onError,
  isLoading = false,
  buttonText = "Connect Gmail Account",
  className = "",
  disabled = false,
  showConsentMessage = true
}) => {
  const [authState, setAuthState] = useState({
    isAuthenticating: false,
    error: null,
    success: false
  });

  const handleTestSuccess = () => {
    const mockOAuthData = {
      access_token: 'mock_access_token_12345',
      refresh_token: 'mock_refresh_token_67890',
      email: 'test@example.com',
      profile: {
        id: 'mock_user_id',
        name: 'Test User',
        picture: 'https://via.placeholder.com/150',
        verified_email: true
      },
      expires_in: 3600,
      token_type: 'Bearer',
      scope: 'https://www.googleapis.com/auth/gmail.readonly openid profile email'
    };

    setAuthState(prev => ({
      ...prev,
      isAuthenticating: false,
      success: true,
      error: null
    }));

    onSuccess?.(mockOAuthData);
  };

  const handleTestError = () => {
    const errorMessage = 'Test error message';
    setAuthState(prev => ({
      ...prev,
      isAuthenticating: false,
      error: errorMessage
    }));
    onError?.(errorMessage);
  };

  /**
   * Start OAuth authentication flow using redirect method
   */
  const startOAuthFlowRedirect = useCallback(async () => {
    try {
      setAuthState(prev => ({ ...prev, isAuthenticating: true, error: null }));
      
      // Generate and store OAuth state for security
      const state = generateOAuthState();
      storeOAuthState(state);
      
      // Gmail OAuth Scopes
      const GMAIL_SCOPES = [
        'https://www.googleapis.com/auth/gmail.readonly',
        'openid',
        'profile',
        'email'
      ];
      
      // Build OAuth URL with PKCE
      const authUrl = await GmailOAuthService.getAuthUrl(GMAIL_SCOPES);
      const urlWithState = `${authUrl}&state=${state}`;
      
      console.log('GmailOAuthSimple: Opening OAuth redirect with URL:', urlWithState);
      
      // Redirect to OAuth URL in same window
      window.location.href = urlWithState;
      
    } catch (error) {
      console.error('OAuth redirect flow error:', error);
      setAuthState(prev => ({
        ...prev,
        isAuthenticating: false,
        error: error.message || 'Failed to start Gmail authentication'
      }));
      onError?.(error.message || 'Failed to start Gmail authentication');
    }
  }, [onError]);

  const isProcessing = authState.isAuthenticating || isLoading;
  const hasError = !!authState.error;
  const isSuccess = authState.success;

  return (
    <div className={`gmail-oauth-component ${className}`}>
      {/* Consent Message */}
      {showConsentMessage && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start">
            <Mail className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Gmail Access Required</p>
              <p className="text-blue-700">
                We need to access your Gmail to automatically extract camp information from your emails. 
                We only read emails and will never send, delete, or modify your messages.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {hasError && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800 mb-1">
                Authentication Error
              </p>
              <p className="text-sm text-red-700 mb-2">
                {authState.error}
              </p>
              <button
                onClick={() => setAuthState(prev => ({ ...prev, error: null }))}
                className="text-xs text-red-600 hover:text-red-800 underline"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {isSuccess && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
            <p className="text-sm text-green-800">
              Gmail account connected successfully!
            </p>
          </div>
        </div>
      )}

      {/* OAuth Buttons */}
      <div className="flex flex-col items-center space-y-3">
        {/* Primary OAuth Button (Test Method) */}
        <button
          onClick={handleTestSuccess}
          disabled={isProcessing || disabled}
          className={`
            relative inline-flex items-center justify-center px-6 py-3 
            border border-transparent text-sm font-medium rounded-lg
            transition-all duration-200 ease-in-out
            focus:outline-none focus:ring-2 focus:ring-offset-2
            ${isProcessing || disabled
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400 focus:ring-blue-500 shadow-sm hover:shadow-md'
            }
            ${isSuccess ? 'bg-green-50 border-green-300 text-green-700' : ''}
            min-w-[200px] h-12
          `}
        >
          {isProcessing ? (
            <>
              <Loader2 className="animate-spin h-5 w-5 mr-2" />
              <span>Connecting...</span>
            </>
          ) : isSuccess ? (
            <>
              <CheckCircle className="h-5 w-5 mr-2" />
              <span>Connected</span>
            </>
          ) : (
            <>
              <Mail className="h-5 w-5 mr-2" />
              <span>{buttonText}</span>
            </>
          )}
        </button>

        {/* Alternative Redirect Method Button */}
        {!isSuccess && !isProcessing && (
          <button
            onClick={startOAuthFlowRedirect}
            disabled={isProcessing || disabled}
            className="
              relative inline-flex items-center justify-center px-4 py-2 
              border border-gray-300 text-sm font-medium rounded-md
              transition-all duration-200 ease-in-out
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
              bg-gray-50 text-gray-600 hover:bg-gray-100 hover:text-gray-700
              shadow-sm hover:shadow-md
              min-w-[180px] h-10
            "
          >
            <span className="text-xs">Try Redirect Method</span>
          </button>
        )}

        {/* Test Buttons */}
        <div className="flex space-x-2">
          <button
            onClick={handleTestSuccess}
            className="text-xs px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
          >
            Test Success
          </button>
          <button
            onClick={handleTestError}
            className="text-xs px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
          >
            Test Error
          </button>
        </div>

        {/* Additional Info */}
        <div className="text-center">
          <p className="text-xs text-gray-500 max-w-sm">
            By connecting your Gmail account, you agree to our terms of service and privacy policy.
            Your email data is processed securely and never shared with third parties.
          </p>
        </div>
      </div>
    </div>
  );
};

export default GmailOAuthSimple;
